import { isApiErrorCode, type ApiErrorParams } from "@ckgo2/shared";
import i18n from "@/i18n";
import { apiErrorText } from "@/lib/apiErrorText";

export const API_BASE = import.meta.env.VITE_API_URL || "https://api.ckgo2.com";

interface ApiErrorResponse {
  error: string;
  message: string;
  success: false;
  details?: { fieldErrors?: Record<string, string[]>; formErrors?: string[] };
  /** 409 optimistic-lock conflict: when the record was last changed on the server. */
  changedAt?: string;
  /** Parameters of a registry-coded error (`{{name}}` etc. in the dictionary text). */
  params?: ApiErrorParams;
}

/** Structured error from API calls. Preserves status, error code, and field-level validation errors. */
export class ApiError extends Error {
  status: number;
  code: string;
  fieldErrors?: Record<string, string[]>;
  /**
   * Validation failures that belong to no single field — a whole-object `.refine()` such as the
   * hotel stay window or the child-ages rule. They have nowhere to highlight, so a banner that
   * only walks `fieldErrors` would drop the reason entirely.
   */
  formErrors?: string[];
  /** Set on a 409 conflict — the server's current updatedAt (Block 6.1 optimistic lock). */
  changedAt?: string;
  /** Parameters of a registry-coded error, as sent by the API. */
  params?: ApiErrorParams;

  constructor(
    message: string,
    status: number,
    code: string,
    fieldErrors?: Record<string, string[]>,
    changedAt?: string,
    formErrors?: string[],
    params?: ApiErrorParams,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.fieldErrors = fieldErrors;
    this.changedAt = changedAt;
    this.formErrors = formErrors;
    this.params = params;
  }

  /** Is `code` a registry code (vize F13.9)? Then `message` is already the dictionary text. */
  get isCoded() {
    return isApiErrorCode(this.code);
  }

  get isConflict() {
    return this.status === 409 && this.code === "conflict";
  }

  get isValidation() {
    return this.status === 400 && this.code === "validation_error";
  }
  get isAuth() {
    return this.status === 401;
  }
  get isForbidden() {
    return this.status === 403;
  }
  get isNotFound() {
    return this.status === 404;
  }
  get isServer() {
    return this.status >= 500;
  }
  get isNetwork() {
    return this.status === 0;
  }
}

// Module-level auth token, synced by authStore via setAuthToken().
// Avoids circular imports (authStore → client, client → authStore).
let _authToken: string | null = null;

/** Called by authStore to keep the in-memory token in sync */
export function setAuthToken(token: string | null): void {
  _authToken = token;
}

/** Returns auth headers for raw fetch calls (e.g., file uploads) */
export function getAuthHeaders(): Record<string, string> {
  return _authToken ? { Authorization: `Bearer ${_authToken}` } : {};
}

/**
 * `ApiError` from a non-2xx response body — the ONE place the client builds it (JSON client
 * and multipart uploads alike). A registry-coded error (vize F13.9) gets its message from the
 * admin dictionary in the active language, parameters included; any other code keeps the
 * server's text (back office stays as it is).
 */
function apiErrorFromBody(
  status: number,
  body: ApiErrorResponse | null,
  fallback: string,
): ApiError {
  const code = body?.error || "unknown";
  const message = isApiErrorCode(code)
    ? apiErrorText(code, body?.params)
    : body?.message || fallback;
  return new ApiError(
    message,
    status,
    code,
    body?.details?.fieldErrors,
    body?.changedAt,
    body?.details?.formErrors,
    body?.params,
  );
}

/**
 * POST multipart/form-data (the JSON `api` client can't send FormData).
 * Adds credentials + auth headers, throws on non-2xx parsing the error body,
 * and returns the parsed JSON. Used by all media-upload call sites.
 */
export async function uploadFormData<T>(path: string, fd: FormData): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method: "POST",
      body: fd,
      credentials: "include",
      headers: getAuthHeaders(),
    });
  } catch {
    // Výpadek sítě: tentýž tvar jako `ApiClient.request` (status 0, opakovatelná chyba).
    throw new ApiError(i18n.t("shared.apiClient.networkError"), 0, "network_error");
  }
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as ApiErrorResponse | null;
    throw apiErrorFromBody(response.status, body, i18n.t("shared.apiClient.uploadFailed"));
  }
  return response.json() as Promise<T>;
}

/**
 * Fetch an authed binary response (PII photo, generated PDF/XML) and return the raw Blob.
 * The JSON `api` client can't hand back binary, and a plain `<img src>`/`<a href>` can't
 * carry the in-memory Bearer token (→ 401), so authed binary goes through here. The caller
 * owns the Blob (e.g. `URL.createObjectURL` + revoke on unmount). To open/download in a tab
 * use `openAuthedFile`, which wraps this.
 */
export async function fetchAuthedBlob(
  path: string,
  init?: { method?: string; body?: unknown },
): Promise<Blob> {
  const response = await fetch(`${API_BASE}${path}`, {
    method: init?.method ?? "GET",
    credentials: "include",
    headers: {
      ...getAuthHeaders(),
      ...(init?.body !== undefined ? { "Content-Type": "application/json" } : {}),
    },
    body: init?.body !== undefined ? JSON.stringify(init.body) : undefined,
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { message?: string } | null;
    throw new Error(
      body?.message || i18n.t("shared.apiClient.loadFileFailed", { status: response.status }),
    );
  }
  return response.blob();
}

/**
 * Fetch an authed binary response and open it in a new tab (or download when `filename` is
 * given). Plain `<a href>` navigation can't carry the in-memory Bearer token (→ 401), so
 * authed file endpoints (invoice PDF, Pohoda XML) go through here.
 */
export async function openAuthedFile(
  path: string,
  filename?: string,
  init?: { method?: string; body?: unknown },
): Promise<void> {
  const blob = await fetchAuthedBlob(path, init);
  const url = URL.createObjectURL(blob);
  if (filename) {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
  } else {
    window.open(url, "_blank");
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    method: string,
    path: string,
    data?: unknown,
    extraHeaders?: Record<string, string>,
  ): Promise<T> {
    const url = `${this.baseUrl}${path}`;

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      ...getAuthHeaders(),
      ...extraHeaders,
    };

    const options: RequestInit = {
      method,
      headers,
      credentials: "include",
    };

    if (data) {
      options.body = JSON.stringify(data);
    }

    let response: Response;
    try {
      response = await fetch(url, options);
    } catch {
      throw new ApiError(i18n.t("shared.apiClient.networkError"), 0, "network_error");
    }

    let json: unknown;
    try {
      json = await response.json();
    } catch {
      throw new ApiError(
        response.ok
          ? i18n.t("shared.apiClient.invalidResponse")
          : i18n.t("shared.apiClient.requestFailedStatus", { status: response.status }),
        response.status,
        "parse_error",
      );
    }

    if (!response.ok) {
      const error = json as ApiErrorResponse;

      // On 401, clear in-memory token — authStore's catch blocks handle the rest
      if (response.status === 401) {
        _authToken = null;
      }

      throw apiErrorFromBody(response.status, error, i18n.t("shared.apiClient.requestFailed"));
    }

    return json as T;
  }

  async get<T>(path: string): Promise<T> {
    return this.request<T>("GET", path);
  }

  async post<T>(path: string, data?: unknown): Promise<T> {
    return this.request<T>("POST", path, data);
  }

  async put<T>(path: string, data?: unknown, headers?: Record<string, string>): Promise<T> {
    return this.request<T>("PUT", path, data, headers);
  }

  async patch<T>(path: string, data?: unknown): Promise<T> {
    return this.request<T>("PATCH", path, data);
  }

  async delete<T>(path: string, data?: unknown): Promise<T> {
    return this.request<T>("DELETE", path, data);
  }
}

export const api = new ApiClient();

// Typed API hooks helpers
export interface ApiResponse<T> {
  data: T;
  success: true;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  success: true;
}
